import React, { useState, useEffect } from 'react';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { ReactComponent as TelephoneIcon } from '../../svgs/telephone.svg';
import { ReactComponent as WhatsappIcon } from '../../svgs/whatsapp.svg';
import { ReactComponent as ChatIcon } from '../../svgs/chat.svg';
import Modal from '../UI/Modal/Modal';
import CallbackForm from './CallbackForm';
import WhatsApp from './WhatsApp';
import ContactOptionsDataLayer from '../Analytics/ContactOptions/ContactOptions';
import * as utils from '../../utils/OrderUtils';
import useOpenHours from '../../hooks/useOpenHours';
import useOrderDetails from '../../hooks/useContactOptionsOrderDetails';
import styles from './ContactOptions.module.scss';
import '../../translations/i18n';

function ContactOptions({
  isExpiredQuote,
  isFailedPayment,
  isPaymentFailedTimeLimit,
  orderStatus,
  isOlderThan30Minutes,
}) {
  const [showCallMeModal, setShowCallMeModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [isFive9Ready, setIsFive9Ready] = useState(false);
  const { t } = useTranslation('common');
  const orderId = window.location.pathname.split('/').pop();
  const { isOpen } = useOpenHours(orderId);
  const { orderData } = useOrderDetails(orderId);
  const isCustomerAccounts = window.location.href.includes('/customer/order');
  const drupalSettings = window.drupalSettings?.cust_account_app || {};
  const domainSettings = drupalSettings.customer_account_contact_options || {};
  const openingHours = drupalSettings.opening_hours;
  const whatsappNumber = utils.getWhatsappNumber();

  useEffect(() => {
    let poller;
    let timeout;

    const shouldAttemptChat = isOpen && isCustomerAccounts && domainSettings?.live_chat === 1;

    if (shouldAttemptChat) {
      // If the widget is already available, set state immediately.
      if (window.Five9SocialWidget) {
        setIsFive9Ready(true);
      } else {
        // Otherwise, poll every 500ms to check for its existence.
        poller = setInterval(() => {
          if (window.Five9SocialWidget) {
            setIsFive9Ready(true);
            clearInterval(poller);
            clearTimeout(timeout);
          }
        }, 500);

        // Stop polling after 10 seconds to avoid an infinite loop if the script fails.
        timeout = setTimeout(() => clearInterval(poller), 10000);
      }
    }

    // Cleanup function to clear interval and timeout on component unmount.
    return () => {
      clearInterval(poller);
      clearTimeout(timeout);
    };
  }, [isOpen, isCustomerAccounts, domainSettings]);

  const closeModals = () => {
    setShowCallMeModal(false);
    setShowWhatsAppModal(false);
  };

  const handleWhatsAppClick = () => {
    closeModals();
    setShowWhatsAppModal(true);
    ContactOptionsDataLayer(orderId, orderStatus, 'WhatsApp');
  };

  const handleLiveChatClick = () => {
    closeModals();
    const five9Element = document.getElementById('five9-maximize-button');
    if (five9Element) {
      five9Element.click();
    }
    ContactOptionsDataLayer(orderId, orderStatus, 'Live Chat');
  };

  const handleCallbackClick = () => {
    closeModals();
    setShowCallMeModal(true);
    ContactOptionsDataLayer(orderId, orderStatus, 'Callback');
  };

  if (!isOpen && !isCustomerAccounts) {
    return null;
  }

  const showWhatsAppButton = !!whatsappNumber && isCustomerAccounts && domainSettings.whatsapp === 1;
  const showLiveChatButton = isOpen && isFive9Ready && isCustomerAccounts && domainSettings.live_chat === 1;

  const hasProblematicStatus = ['quote', 'in_checkout', 'payment_failed'].includes(orderStatus)
    || isFailedPayment
    || isPaymentFailedTimeLimit;

  const showCallbackForAccount = isCustomerAccounts && domainSettings.callback === 1 && hasProblematicStatus;
  const showCallbackForCheckout = !isCustomerAccounts && isOpen;
  const shouldShowCallbackButton = showCallbackForAccount || showCallbackForCheckout;

  const showCallbackAsFallback = !showWhatsAppButton && !showLiveChatButton;

  const hasOrderIssues = (isExpiredQuote && orderStatus === 'quote')
    || isFailedPayment
    || isPaymentFailedTimeLimit
    || (orderStatus === 'in_checkout' && isOlderThan30Minutes && utils.getCookieValue('feature-CHEC-676-2step-checkout') === '1');

  const renderContactInfo = () => {
    if (isCustomerAccounts) {
      let message;

      if (hasOrderIssues) {
        if (isPaymentFailedTimeLimit || isFailedPayment) {
          message = t('contact_info_payment_failed');
        } else {
          message = t('contact_info_issues');
        }
      } else {
        message = t('contact_info_no_issues');
      }

      return <div className={styles.contactInfo}><p>{parse(message)}</p></div>;
    }
    return (
      <>
        <h1 className={styles.contactTitle}>{t('contact_options.title')}</h1>
        <p className={styles.contactSubtitle}>{t('contact_options.subtitle')}</p>
      </>
    );
  };

  return (
    <div className={styles.contactOptionsWrapper}>
      <h2 className={styles.title}>{t('contact_tab')}</h2>
      {renderContactInfo()}

      <div className={`${styles.contactButtonsContainer} ${styles.mobileStack}`}>
        {showWhatsAppButton && (
          <button type="button" className={`${styles.contactOptionButton} ${styles.whatsapp}`} onClick={handleWhatsAppClick}>
            <div className={styles.contactOptionIcon}>
              <WhatsappIcon />
              <span className={styles.contactOptionText}>{t('contact_options.whatsapp_text')}</span>
            </div>
          </button>
        )}
        {showLiveChatButton && (
          <button type="button" className={`${styles.contactOptionButton} ${styles.chat}`} onClick={handleLiveChatClick}>
            <div className={styles.contactOptionIcon}>
              <ChatIcon />
              <span className={styles.contactOptionText}>{t('contact_options.live_chat_text')}</span>
            </div>
          </button>
        )}
        {(shouldShowCallbackButton || showCallbackAsFallback) && (
          <button type="button" className={`${styles.contactOptionButton} ${styles.callback}`} onClick={handleCallbackClick}>
            <div className={styles.contactOptionIcon}>
              <TelephoneIcon />
              <span className={styles.contactOptionText}>{t('contact_options.callback_text')}</span>
            </div>
          </button>
        )}
      </div>

      {openingHours && (
        <>
          <b className={styles.openingHoursTitle}>{t('Opening Hours')}</b>
          <div className={styles.openingHours}>{parse(openingHours)}</div>
        </>
      )}

      {showCallMeModal && (
        <Modal isOpen onClose={closeModals} title={t('contact_options.callback_text')} icon={<span className={styles.icon}><TelephoneIcon /></span>}>
          <CallbackForm orderId={orderId} orderData={orderData} isCustomerAccounts={isCustomerAccounts} />
        </Modal>
      )}

      {whatsappNumber && showWhatsAppModal && (
        <Modal isOpen onClose={closeModals} title={t('contact_options.whatsapp_text')} icon={<span className={styles.icon}><WhatsappIcon /></span>}>
          <WhatsApp />
        </Modal>
      )}
    </div>
  );
}

export default ContactOptions;
